import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import type { FC } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@/features/post-editor/components/DateTimePicker';
import { PostEditorModalShell } from '@/features/post-editor/components/editor/PostEditorModalShell';
import { PostForm } from '@/features/post-editor/constants/form';
import { useGetPostTypeFeature } from '@/features/post-editor/hooks/useGetPostFeature';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';

type Props = {
  isVisible: boolean;
  onClose: () => void;
};

/** Bottom modal for picking the happening date/time range. */
export const PostDateModal: FC<Props> = ({ isVisible, onClose }) => {
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const { control } = useFormContext<PostFormValues>();

  const channelType = useWatch({ control, name: 'channelType' });
  const feature = useGetPostTypeFeature(channelType);
  const maximumDurationDays =
    feature.featureValues?.additionalCustomAttributes?.maximumDurationDays;

  const allDay = useController({ control, name: 'allDay' });
  const startDate = useController({ control, name: 'startDate' });
  const endDate = useController({ control, name: 'endDate' });

  return (
    <PostEditorModalShell
      isVisible={isVisible}
      title={t(PostForm.DATE)}
      onClose={onClose}
      onBackdropPress={onClose}
      maxDynamicContentSizeRatio={0.7}
      fillScrollContent={false}
      stickyHeader
      scrollComponent={BottomSheetScrollView}
    >
      <DateTimePicker
        allDay={allDay.field.value}
        startDate={startDate.field.value}
        endDate={endDate.field.value}
        setAllDay={allDay.field.onChange}
        setStartDate={startDate.field.onChange}
        setEndDate={endDate.field.onChange}
        maximumDurationDays={maximumDurationDays}
      />
    </PostEditorModalShell>
  );
};
